/**
 * R18-9 — Idle warning banner.
 *
 * Shown when the backend's idle sweeper emits `idle_warning` via LiveKit
 * data channel. Includes an "Extend room" button for the host that resets
 * the idle clock (rate-limited server-side to once per 10 min). Audience
 * just sees the timer; no action required.
 *
 * Testids:
 *   - idle-warning-banner
 *   - idle-extend-room-btn
 */
import React from "react";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";

export default function IdleBanner({
  visible, isHost, minutesUntilEnd, busyExtending, onExtend, onDismiss,
}) {
  if (!visible) return null;
  return (
    <div
      className="sticky top-0 z-30 mx-auto max-w-5xl mt-2 mb-2 rounded-lg border border-amber-400/35 bg-amber-500/10 backdrop-blur px-4 py-2.5 flex items-center gap-3"
      data-testid="idle-warning-banner"
    >
      <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
      <div className="flex-1 text-xs text-amber-100">
        <strong className="text-amber-200">DJ has been silent</strong>
        {" — room will auto-end in "}
        <strong>{minutesUntilEnd != null ? `${minutesUntilEnd} min` : "a few minutes"}</strong>
        {isHost ? " unless extended." : ". Sit tight or refresh later."}
      </div>
      {isHost && (
        <button
          onClick={onExtend}
          disabled={busyExtending}
          className="px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-amber-500/80 hover:bg-amber-500 text-black disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="idle-extend-room-btn"
        >
          {busyExtending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Extend room
        </button>
      )}
      {!isHost && (
        <button
          onClick={onDismiss}
          className="text-[10px] uppercase tracking-[0.14em] text-amber-200/65 hover:text-amber-200 px-2"
          data-testid="idle-banner-dismiss-btn"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
